import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { TimesheetSummary } from "../../api";
import { Card, EmptyState, Table } from "../../components";
import type { TableColumn } from "../../components";
import { formatDateTime, formatTokens } from "../../format";
import { StatutBadge } from "./FeuilleStatut";
import { formatJours, libelleMois, parseMois } from "./mois";
import styles from "./Feuilles.module.css";

export type FeuillesTableProps = {
  rows: TimesheetSummary[];
  /** Où mène une ligne : la fiche de la feuille dans l'espace courant. */
  lienVers: (sheetId: string) => string;
  /** Afficher la colonne des montants approuvés. Faux pour un consultant. */
  montants: boolean;
  /** Afficher la colonne de l'entreprise (ESN). */
  entreprise?: boolean;
  /** Afficher la colonne du consultant. Faux dans l'espace consultant. */
  consultant?: boolean;
  caption: string;
  loading?: boolean;
  vide: { titre: string; description: string };
};

/** Le mois d'un résumé, en français. */
function moisDe(row: TimesheetSummary): string {
  const m = parseMois(row.period_month);
  return m === null ? row.period_month : libelleMois(m);
}

/** La liste des feuilles, la même dans les trois espaces à quelques colonnes près. */
export function FeuillesTable({
  rows,
  lienVers,
  montants,
  entreprise = false,
  consultant = true,
  caption,
  loading = false,
  vide,
}: FeuillesTableProps) {
  const colonnes = useMemo<TableColumn<TimesheetSummary>[]>(() => {
    const cols: TableColumn<TimesheetSummary>[] = [
      {
        key: "mois",
        header: "Mois",
        render: (r) => (
          <Link to={lienVers(r.id)} className={styles.lienLigne}>
            {moisDe(r)}
          </Link>
        ),
      },
      { key: "mission", header: "Mission", render: (r) => r.mission_label },
    ];
    if (entreprise) {
      cols.push({ key: "entreprise", header: "Entreprise", render: (r) => r.company_name });
    }
    if (consultant) {
      cols.push({ key: "consultant", header: "Consultant", render: (r) => r.consultant_email });
    }
    cols.push(
      { key: "statut", header: "Statut", render: (r) => <StatutBadge statut={r.status} size="sm" /> },
      { key: "jours", header: "Jours", numeric: true, render: (r) => formatJours(r.total_half_days) },
    );
    if (montants) {
      cols.push({
        key: "tokens",
        header: "Tokens imputés",
        numeric: true,
        render: (r) => (r.approved_tokens !== undefined ? formatTokens(r.approved_tokens) : "—"),
      });
    }
    cols.push({
      key: "soumise",
      header: "Soumise le",
      render: (r) => (r.submitted_at === null ? "—" : formatDateTime(r.submitted_at)),
    });
    return cols;
  }, [lienVers, montants, entreprise, consultant]);

  return (
    <Card padding="none">
      <Table
        columns={colonnes}
        rows={rows}
        rowKey={(r) => r.id}
        caption={caption}
        loading={loading}
        animateRows={false}
        empty={<EmptyState compact title={vide.titre} description={vide.description} />}
      />
    </Card>
  );
}
